import { ArrowDown, ArrowUp, Minus, type Icon } from '@phosphor-icons/react';
import { Button, Text } from '@fragments-sdk/ui';
import type { GovernanceCiStatus } from './dashboard-brief';
import s from './dashboard.module.scss';

export interface StatTileProps {
  label: string;
  icon?: Icon;
  value: number;
  deltaValue: number;
  deltaLabel: string;
  direction: 'up-good' | 'down-good';
  ciStatus?: GovernanceCiStatus;
  onClick?: () => void;
}

export function StatTile({
  label,
  icon: TileIcon,
  value,
  deltaValue,
  deltaLabel,
  direction,
  ciStatus,
  onClick,
}: StatTileProps) {
  // Coerce non-finite deltas (NaN, undefined, Infinity) to zero so upstream
  // data gaps don't render as "−NaN" in the UI.
  const safeDelta = Number.isFinite(deltaValue) ? deltaValue : 0;
  const isZero = safeDelta === 0;
  const isPositive = safeDelta > 0;
  const good = direction === 'up-good' ? isPositive : !isPositive && !isZero;
  const deltaTone = isZero ? 'neutral' : good ? 'good' : 'bad';
  const DeltaIcon = isZero ? Minus : isPositive ? ArrowUp : ArrowDown;

  const inner = (
    <>
      <span className={s.statHeader}>
        {TileIcon ? (
          <span className={s.statIcon}>
            <TileIcon size={12} weight="regular" />
          </span>
        ) : null}
        <Text
          as="span"
          size="xs"
          weight="medium"
          color="tertiary"
          className={s.statLabel}
        >
          {label}
        </Text>
      </span>
      <Text
        as="span"
        size="xl"
        weight="medium"
        tabularNums
        letterSpacing="tightest"
        className={s.statValue}
      >
        {value}
      </Text>
      <span className={s.statFooter}>
        <Text
          as="span"
          size="2xs"
          weight="medium"
          tabularNums
          className={s.statDelta}
          data-tone={deltaTone}
        >
          <DeltaIcon size={10} weight="bold" />
          {!isZero ? Math.abs(safeDelta) : '0'}
        </Text>
        <Text
          as="span"
          size="2xs"
          color="tertiary"
          tabularNums
          className={s.statDeltaLabel}
        >
          {ciStatus === 'not_configured' ? 'set up governance' : deltaLabel}
        </Text>
      </span>
    </>
  );

  if (onClick) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className={s.statTile}
        data-clickable="true"
        onClick={onClick}
      >
        {inner}
      </Button>
    );
  }
  return <div className={s.statTile}>{inner}</div>;
}
